import * as React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ColorMode = 'system' | 'light' | 'dark';

interface ColorModeContextType {
    mode: ColorMode;
    setMode: (mode: ColorMode) => void;
}

const ColorModeContext = React.createContext<ColorModeContextType | undefined>(undefined);

export const useColorMode = () => {
    const context = React.useContext(ColorModeContext);
    if (!context) {
        throw new Error('useColorMode must be used within a ColorModeProvider');
    }
    return context;
};

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = React.useState<ColorMode>(() => {
        // Get initial mode from localStorage or default to 'system'
        const saved = localStorage.getItem('color-mode');
        return (saved as ColorMode) || 'system';
    });

    // Apply theme to document
    React.useEffect(() => {
        const root = document.documentElement;

        // Remove existing theme classes
        root.classList.remove('light', 'dark');

        if (mode === 'system') {
            // Check system preference
            const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (systemPrefersDark) {
                root.classList.add('dark');
            } else {
                root.classList.add('light');
            }
        } else {
            // Apply explicit theme
            root.classList.add(mode);
        }

        // Save to localStorage
        localStorage.setItem('color-mode', mode);
    }, [mode]);

    // Listen for system theme changes when in system mode
    React.useEffect(() => {
        if (mode !== 'system') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
            const root = document.documentElement;
            root.classList.remove('light', 'dark');

            if (mediaQuery.matches) {
                root.classList.add('dark');
            } else {
                root.classList.add('light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [mode]);

    const value = React.useMemo(() => ({ mode, setMode }), [mode]);

    return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export default function ColorModeSelect() {
    const { mode, setMode } = useColorMode();

    const handleModeChange = (newMode: ColorMode) => {
        setMode(newMode);
    };

    const getIcon = (mode: ColorMode) => {
        switch (mode) {
            case 'light':
                return <Sun className="h-4 w-4" />;
            case 'dark':
                return <Moon className="h-4 w-4" />;
            default:
                return <Monitor className="h-4 w-4" />;
        }
    };

    const getLabel = (mode: ColorMode) => {
        switch (mode) {
            case 'light':
                return 'Light';
            case 'dark':
                return 'Dark';
            default:
                return 'System';
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="w-[120px] justify-between"
                    data-screenshot="toggle-mode"
                >
                    {getIcon(mode)}
                    {getLabel(mode)}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleModeChange('system')}>
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleModeChange('light')}>
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleModeChange('dark')}>
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
